/**
 * Comptes enfants GÉRÉS par le parent + bascule de compte sur le même
 * téléphone (migration 0301). Le parent crée le compte de son enfant (sans
 * email), passe dessus pour le laisser réviser, et revient sur son compte avec
 * son code PIN.
 *
 * Une seule session sur l'appareil : chaque bascule REMPLACE la session
 * (`setSession`) par celle que le serveur vient de frapper, puis recharge la
 * page sur l'espace du compte (cache React Query vidé, aucun écran de l'autre
 * compte ne reste affiché). En mode enfant, aucun jeton parent n'est gardé :
 * le retour passe obligatoirement par `parent-switch-back` (PIN vérifié en SQL).
 *
 * RPC / colonnes postérieures aux types générés → client générique.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const PIN_PATTERN = /^\d{4}$/;

/** Slug d'erreur d'une edge fn (`{ error }` dans le corps JSON). */
async function invokeErrorSlug(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (!ctx || typeof ctx.json !== 'function') return null;
  try {
    const body = (await ctx.json()) as { error?: string; message?: string };
    return body?.message ?? body?.error ?? null;
  } catch {
    return null;
  }
}

/** Code d'erreur serveur → phrase pour le parent / l'enfant. */
export function managedChildErrorMessage(code: string | null): string {
  switch (code) {
    case 'wrong_pin':
      return 'Code incorrect.';
    case 'pin_locked':
      return 'Trop d’essais : réessaie dans 15 minutes, ou reconnecte-toi par email.';
    case 'no_pin':
    case 'pin_required':
      return 'Choisis d’abord ton code parent.';
    case 'not_managed':
      return 'Ce compte n’a pas été créé depuis ton espace parent.';
    case 'too_many_children':
      return 'Tu as atteint le nombre maximal de comptes enfants.';
    case 'not_a_parent':
      return 'Seul un compte parent peut faire cela.';
    case null:
    case '':
      return 'Une erreur est survenue. Réessaie.';
    default:
      // Message de validation déjà rédigé par le serveur (prénom, âge…).
      return /\s/.test(code) ? code : 'Une erreur est survenue. Réessaie.';
  }
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw new Error(managedChildErrorMessage(await invokeErrorSlug(error)));
  return data as T;
}

interface SessionTokens {
  access_token: string;
  refresh_token: string;
}

/** Remplace la session de l'appareil, puis recharge sur l'espace du compte. */
async function adoptSession(tokens: SessionTokens, path: string): Promise<void> {
  const { error } = await supabase.auth.setSession(tokens);
  if (error) throw new Error('La bascule a échoué. Réessaie.');
  window.location.assign(path);
}

export interface NewChildInput {
  display_name: string;
  birthdate: string;
  level: string;
  subjects: string[];
}

export function useCreateManagedChild(parentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewChildInput) =>
      invoke<{ child_id: string; display_name: string }>('parent-create-child', { ...input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parent-children', parentId] });
      qc.invalidateQueries({ queryKey: ['managed-children', parentId] });
    },
  });
}

/** Ids des enfants dont CE parent a créé le compte (seuls accessibles par bascule). */
export function useManagedChildIds(parentId: string) {
  return useQuery({
    queryKey: ['managed-children', parentId],
    enabled: !!parentId,
    staleTime: 60_000,
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await db
        .from('student_profiles')
        .select('id')
        .eq('managed_by_parent', parentId);
      if (error) throw new Error(error.message);
      return new Set(((data ?? []) as { id: string }[]).map((r) => r.id));
    },
  });
}

export function useHasSwitchPin(enabled: boolean) {
  return useQuery({
    queryKey: ['parent-switch-pin'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await db.rpc('has_parent_switch_pin');
      if (error) throw new Error(error.message);
      return data === true;
    },
  });
}

export function useSetSwitchPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pin: string) => {
      if (!PIN_PATTERN.test(pin)) throw new Error('Le code doit avoir 4 chiffres.');
      const { error } = await db.rpc('set_parent_switch_pin', { p_pin: pin });
      if (error) throw new Error(managedChildErrorMessage(error.message));
    },
    onSuccess: () => qc.setQueryData(['parent-switch-pin'], true),
  });
}

/** Parent → enfant : le téléphone passe sur le compte de l'enfant. */
export async function switchToChild(childId: string): Promise<void> {
  const tokens = await invoke<SessionTokens>('parent-switch-to-child', { child_id: childId });
  await adoptSession(tokens, '/student/dashboard');
}

/** Enfant → parent : exige le code PIN du parent (vérifié côté serveur). */
export async function switchBackToParent(pin: string): Promise<void> {
  if (!PIN_PATTERN.test(pin)) throw new Error('Le code doit avoir 4 chiffres.');
  const tokens = await invoke<SessionTokens>('parent-switch-back', { pin });
  await adoptSession(tokens, '/parent');
}

/**
 * Prénom du parent qui gère le compte de l'élève connecté, ou null si ce
 * compte n'est pas géré (élève autonome) : décide d'afficher le bouton
 * « Revenir au compte parent ».
 */
export function useManagingParent(studentId: string | undefined) {
  return useQuery({
    queryKey: ['managing-parent', studentId],
    enabled: !!studentId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await db
        .from('student_profiles')
        .select('managed_by_parent')
        .eq('id', studentId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      const parentId = (data as { managed_by_parent?: string | null } | null)?.managed_by_parent;
      return parentId ?? null;
    },
  });
}
